import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import SettingsIcon from '@mui/icons-material/Settings';
import { generateObjectIdLike } from '../../util/objectId';

/**
 * Extends the plain freeform "Demographics Tracking Categories" chip input
 * (Autocomplete freeSolo multiple, unchanged below) with a per-category
 * "configure" affordance for `demographicCategoryConfigs`: predefined
 * options + an allow-freeform toggle. Works standalone — no template
 * required, exactly like today's freeform categories, just richer.
 *
 * `categories` stays a plain string[] (the legacy field); `configs` is the
 * additive demographicCategoryConfigs array. Both are lifted state, changed
 * together via onChange(nextCategories, nextConfigs).
 */
export default function CategoryConfigEditor({ categories, configs, onChange, disabled }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [editingCategory, setEditingCategory] = React.useState(null);

  const safeCategories = categories || [];
  const safeConfigs = configs || [];

  const configByName = (name) => safeConfigs.find((c) => c && c.name === name);

  const handleCategoriesChange = (newValue) => {
    const trimmed = newValue.map((v) => v.trim()).filter(Boolean);
    // Drop configs for categories that were removed, keep everything else untouched.
    const nextConfigs = safeConfigs.filter((c) => trimmed.indexOf(c.name) >= 0);
    onChange(trimmed, nextConfigs);
  };

  const openConfig = (event, categoryName) => {
    setEditingCategory(categoryName);
    setAnchorEl(event.currentTarget);
  };

  const closeConfig = () => {
    setAnchorEl(null);
    setEditingCategory(null);
  };

  const updateConfig = (categoryName, updates) => {
    const existing = configByName(categoryName);
    let nextConfigs;
    if (existing) {
      nextConfigs = safeConfigs.map((c) => (c.name === categoryName ? { ...c, ...updates } : c));
    } else {
      nextConfigs = [
        ...safeConfigs,
        {
          _id: generateObjectIdLike(),
          name: categoryName,
          options: [],
          allowFreeform: true,
          sourceTemplateId: null,
          ...updates,
        },
      ];
    }
    onChange(safeCategories, nextConfigs);
  };

  const editingConfig = editingCategory
    ? (configByName(editingCategory) || { options: [], allowFreeform: true })
    : null;

  return (
    <React.Fragment>
      <Autocomplete
        multiple
        freeSolo
        options={[]}
        disabled={disabled}
        value={safeCategories}
        onChange={(event, newValue) => handleCategoriesChange(newValue)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} key={option} />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} variant="outlined" size="small" label="Demographics Tracking Categories" />
        )}
      />
      <Typography variant="caption" color="text.secondary" component="div">
        Press <code>Enter</code> to create a category
      </Typography>
      {safeCategories.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
            Configure predefined options per category (optional):
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {safeCategories.map((category) => {
              const config = configByName(category);
              const hasOptions = config && config.options && config.options.length > 0;
              const isClosed = config && config.allowFreeform === false;
              return (
                <Chip
                  key={category}
                  label={category}
                  size="small"
                  icon={<SettingsIcon fontSize="small" />}
                  variant={hasOptions || isClosed ? 'filled' : 'outlined'}
                  color={hasOptions || isClosed ? 'primary' : 'default'}
                  disabled={disabled}
                  onClick={(event) => openConfig(event, category)}
                />
              );
            })}
          </Stack>
        </Box>
      )}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeConfig}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {editingCategory && editingConfig && (
          <Box sx={{ p: 2, minWidth: 280 }}>
            <Typography variant="subtitle2" gutterBottom>{editingCategory}</Typography>
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={[]}
              value={editingConfig.options || []}
              onChange={(event, newValue) => {
                updateConfig(editingCategory, {
                  options: newValue.map((v) => v.trim()).filter(Boolean),
                });
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" label="Predefined options" />
              )}
            />
            <Typography variant="caption" color="text.secondary" component="div">
              Press <code>Enter</code> to create an option
            </Typography>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  size="small"
                  checked={editingConfig.allowFreeform !== false}
                  onChange={(event) => updateConfig(editingCategory, { allowFreeform: event.target.checked })}
                />
              }
              label="Allow other values (freeform)"
            />
          </Box>
        )}
      </Popover>
    </React.Fragment>
  );
}
